import React, { useState, useEffect, useRef } from 'react';
import { Wifi, CheckCircle, Loader } from 'lucide-react';
import { offlineSync, SyncStatus } from '../utils/offlineSync';
import { useTranslation } from '../i18n/hooks/useTranslation';

export const OnlineSyncModal: React.FC = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>(offlineSync.getStatus());
  const [, setWasOffline] = useState(false);
  const [syncComplete, setSyncComplete] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isVisibleRef = useRef(isVisible);
  const syncCompleteRef = useRef(syncComplete);
  
  // Keep refs in sync with state
  useEffect(() => {
    isVisibleRef.current = isVisible;
  }, [isVisible]);
  
  useEffect(() => {
    syncCompleteRef.current = syncComplete;
  }, [syncComplete]);

  useEffect(() => {
    const clearCurrentTimeout = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    // Track if we were offline
    const handleOffline = () => {
      setWasOffline(true);
      setIsVisible(false);
      setSyncComplete(false);
      clearCurrentTimeout();
    };

    // Handle coming back online
    const handleOnline = () => {
      setWasOffline((prevWasOffline) => {
        if (prevWasOffline) {
          const currentStatus = offlineSync.getStatus();
          setIsVisible(true);
          setSyncComplete(false);
          setSyncStatus(currentStatus);
          
          // Start syncing
          offlineSync.syncAll();
          
          // If no pending items, show success immediately
          if (currentStatus.pendingCount === 0) {
            setSyncComplete(true);
            timeoutRef.current = setTimeout(() => {
              setIsVisible(false);
              setSyncComplete(false);
            }, 2000);
          }
        }
        return false; // We're now online
      });
    };

    // Initial state - check both navigator.onLine and sync status
    const initialStatus = offlineSync.getStatus();
    setWasOffline(!initialStatus.isOnline);
    let previousStatusRef = { ...initialStatus };

    // Subscribe to offline sync status changes
    const unsubscribe = offlineSync.subscribe((status) => {
      const previousStatus = previousStatusRef;
      previousStatusRef = { ...status };
      setSyncStatus(status);
      
      // Handle transition from offline to online (via status update, not just browser event)
      if (!previousStatus.isOnline && status.isOnline) {
        // Only show modal if we were actually offline before
        setWasOffline((prevWasOffline) => {
          if (prevWasOffline) {
            setIsVisible(true);
            setSyncComplete(false);
            
            // Start syncing
            offlineSync.syncAll();
            
            // If no pending items, show success immediately
            if (status.pendingCount === 0) {
              setSyncComplete(true);
              clearCurrentTimeout();
              timeoutRef.current = setTimeout(() => {
                setIsVisible(false);
                setSyncComplete(false);
              }, 2000);
            }
            return false; // We're now online
          }
          return prevWasOffline;
        });
      }
      
      // Handle transition to offline
      if (previousStatus.isOnline && !status.isOnline) {
        setWasOffline(true);
        setIsVisible(false);
        setSyncComplete(false);
        clearCurrentTimeout();
      }
      
      // If sync completes and we're visible, show success then hide
      if (isVisibleRef.current && status.pendingCount === 0 && status.isOnline && !syncCompleteRef.current) {
        setSyncComplete(true);
        clearCurrentTimeout();
        timeoutRef.current = setTimeout(() => {
          setIsVisible(false);
          setSyncComplete(false);
        }, 2000);
      }
      
      // If we've been syncing for more than 30 seconds and still have pending items,
      // it might be stuck - allow user to dismiss
      if (isVisibleRef.current && status.pendingCount > 0 && status.isOnline && !syncCompleteRef.current) {
        // Set a timeout to allow dismissal after 30 seconds
        if (!timeoutRef.current) {
          timeoutRef.current = setTimeout(() => {
            // Don't auto-dismiss, but allow manual dismissal
            console.log('[OnlineSyncModal] Sync taking longer than expected, allowing dismissal');
          }, 30000);
        }
      }
    });

    // Listen to browser online/offline events
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      unsubscribe();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearCurrentTimeout();
    };
  }, []);

  if (!isVisible) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 transform transition-all duration-300 scale-100">
        <div className="flex flex-col items-center text-center space-y-4">
          {/* Icon */}
          <div className={`relative w-20 h-20 rounded-full flex items-center justify-center transition-all duration-300 ${
            syncComplete 
              ? 'bg-success-100 dark:bg-success-900/30' 
              : 'bg-accent-100 dark:bg-accent-900/30'
          }`}>
            {syncComplete ? (
              <CheckCircle className="w-12 h-12 text-success-600 dark:text-success-400" />
            ) : syncStatus.pendingCount > 0 ? (
              <Loader className="w-12 h-12 text-accent-600 dark:text-accent-400 animate-spin" />
            ) : (
              <Wifi className="w-12 h-12 text-accent-600 dark:text-accent-400" />
            )}
          </div>

          {/* Title */}
          <div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              {syncComplete 
                ? t('offline.backOnlineSyncComplete') || 'All synced!'
                : t('offline.backOnline') || 'We\'re back online!'
              }
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {syncComplete 
                ? t('offline.syncCompleteMessage') || 'All your changes have been synced successfully.'
                : syncStatus.pendingCount > 0
                  ? t('offline.syncingMessage', { 
                      count: syncStatus.pendingCount, 
                      plural: syncStatus.pendingCount === 1 ? '' : 's' 
                    }) || `Syncing ${syncStatus.pendingCount} timesheet${syncStatus.pendingCount === 1 ? '' : 's'}...`
                  : t('offline.checkingSync') || 'Checking for changes to sync...'
              }
            </p>
          </div>

          {/* Progress indicator */}
          {syncStatus.pendingCount > 0 && !syncComplete && (
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
              <div 
                className="bg-accent-600 dark:bg-accent-500 h-2 rounded-full transition-all duration-500 animate-pulse"
                style={{ width: '60%' }}
              />
            </div>
          )}

          {/* Dismiss button */}
          <div className="mt-4 flex gap-2">
            {syncComplete ? (
              <button
                onClick={() => setIsVisible(false)}
                className="px-6 py-2 bg-accent-600 hover:bg-accent-700 text-white rounded-lg transition-colors font-medium"
              >
                {t('common.close') || 'Close'}
              </button>
            ) : (
              <button
                onClick={() => {
                  setIsVisible(false);
                  setSyncComplete(false);
                }}
                className="px-6 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors font-medium"
              >
                {t('common.dismiss') || 'Dismiss'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

